/**
 * Public In Memoriam directory — deceased notable people sourced from Wikipedia.
 * Wikipedia content is CC BY-SA. Each entry links back to its canonical Wikipedia
 * page for attribution. Our AI-generated 150-word memorial paragraph appears on
 * the card + detail view.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { Flower, ExternalLink, Search, MapPin } from "lucide-react";
import { Helmet } from "react-helmet-async";

const currentYear = new Date().getUTCFullYear();
const YEARS = [currentYear, currentYear - 1, currentYear - 2, currentYear - 3];

function MemoriamCard({ person }) {
  const yearsText = [person.born, person.died].filter(Boolean).join(" – ");
  return (
    <article
      data-testid={`memoriam-card-${person.slug || person.id}`}
      className="wofy-card flex flex-col overflow-hidden"
    >
      <div
        className="w-full h-56 bg-cover bg-center"
        style={{
          backgroundImage: `url(${person.photo_url || "/wofy-og-fallback.png"})`,
          backgroundColor: "var(--wofy-surface)",
        }}
      />
      <div className="p-5 flex-1 flex flex-col">
        <div className="overline mb-2" style={{ color: "var(--wofy-primary)" }}>
          {yearsText || "In Memoriam"}
        </div>
        <h3 className="font-display text-xl leading-tight">
          {person.name}
        </h3>
        {person.occupation && (
          <p className="mt-1 text-xs" style={{ color: "var(--wofy-text-soft)" }}>
            {person.occupation}
          </p>
        )}
        {person.in_memoriam_summary ? (
          <p className="mt-3 text-sm leading-relaxed line-clamp-6" style={{ color: "var(--wofy-text)" }}>
            {person.in_memoriam_summary}
          </p>
        ) : person.tagline ? (
          <p className="mt-3 text-sm leading-relaxed" style={{ color: "var(--wofy-text-soft)" }}>
            {person.tagline}
          </p>
        ) : (
          <p className="mt-3 text-sm italic" style={{ color: "var(--wofy-text-soft)" }}>
            Memorial summary being prepared…
          </p>
        )}
        <div className="mt-4 pt-3 border-t flex items-center justify-between text-xs" style={{ borderColor: "var(--wofy-border)" }}>
          <Link
            to={`/world-changers/${person.slug || person.id}`}
            className="wofy-btn-ghost text-xs"
            data-testid={`memoriam-open-${person.slug || person.id}`}
          >
            Read tribute →
          </Link>
          {person.wiki_url && (
            <a
              href={person.wiki_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 opacity-70 hover:opacity-100"
              style={{ color: "var(--wofy-text-soft)" }}
              data-testid={`memoriam-wiki-${person.slug || person.id}`}
            >
              Wikipedia <ExternalLink className="w-3 h-3" />
            </a>
          )}
          {person.commemoration_url && !person.wiki_url && (
            <a
              href={person.commemoration_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 opacity-70 hover:opacity-100"
              style={{ color: "var(--wofy-text-soft)" }}
              data-testid={`memoriam-cwgc-${person.slug || person.id}`}
            >
              CWGC <ExternalLink className="w-3 h-3" />
            </a>
          )}
          {person.canonical_url && !person.wiki_url && !person.commemoration_url && (
            <a
              href={person.canonical_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 opacity-70 hover:opacity-100"
              style={{ color: "var(--wofy-text-soft)" }}
              data-testid={`memoriam-fs-${person.slug || person.id}`}
            >
              FamilySearch <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>
      </div>
    </article>
  );
}

export default function InMemoriam() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [year, setYear] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (q) params.q = q;
    if (year) params.year = year;
    http.get("/in-memoriam", { params })
      .then((r) => setItems(Array.isArray(r.data) ? r.data : []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [q, year]);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const p of items) {
      const y = (p.died || "").slice(0, 4) || "Unknown";
      if (!map.has(y)) map.set(y, []);
      map.get(y).push(p);
    }
    return Array.from(map.entries()).sort((a, b) => b[0].localeCompare(a[0]));
  }, [items]);

  return (
    <div data-testid="in-memoriam-page" className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
      <Helmet>
        <title>In Memoriam — Social Pulse</title>
        <meta name="description" content="Remembering notable people who have passed away. A respectful, Wikipedia-sourced tribute directory." />
        <link rel="canonical" href="https://socialpulse.com/in-memoriam" />
        <meta property="og:title" content="In Memoriam — Social Pulse" />
        <meta property="og:description" content="Remembering notable people who have passed away." />
        <meta property="og:url" content="https://socialpulse.com/in-memoriam" />
      </Helmet>

      <div className="flex items-center gap-3 mb-3">
        <Flower className="w-5 h-5" style={{ color: "var(--wofy-primary)" }} />
        <div className="overline">A GLOBAL TRIBUTE</div>
      </div>
      <h1 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
        In Memoriam.
      </h1>
      <p className="mt-4 max-w-2xl text-base" style={{ color: "var(--wofy-text-soft)" }}>
        A respectful, continuously-updated tribute to notable people who have passed away — sourced
        from Wikipedia (CC BY-SA) and enriched with a short editorial remembrance. If your loved
        one is missing, families can{" "}
        <Link to="/celebrate/submit" className="underline">submit a personal memorial</Link>.
      </p>

      {/* Filter bar */}
      <div
        data-testid="memoriam-filter-bar"
        className="mt-8 flex flex-wrap items-center gap-3 p-4 rounded-2xl border"
        style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-surface)" }}
      >
        <div className="flex-1 min-w-[240px] relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--wofy-text-soft)" }} />
          <input
            type="search"
            data-testid="memoriam-search-input"
            placeholder="Search by name, role, or country…"
            className="w-full pl-9 pr-3 py-2 rounded-full border text-sm bg-transparent"
            style={{ borderColor: "var(--wofy-border)" }}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs font-mono uppercase tracking-wider" style={{ color: "var(--wofy-text-soft)" }}>Year</span>
          <select
            data-testid="memoriam-year-select"
            value={year}
            onChange={(e) => setYear(e.target.value)}
            className="px-3 py-1.5 rounded-full border text-sm bg-transparent"
            style={{ borderColor: "var(--wofy-border)" }}
          >
            <option value="">All years</option>
            {YEARS.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
        <div className="text-xs font-mono" style={{ color: "var(--wofy-text-soft)" }}>
          {loading ? "Loading…" : `${items.length} tribute${items.length === 1 ? "" : "s"}`}
        </div>
      </div>

      {/* Grouped by year */}
      <div className="mt-10 space-y-14">
        {grouped.length === 0 && !loading && (
          <div className="wofy-card text-center py-16" data-testid="memoriam-empty">
            <div className="font-display text-2xl">No tributes match your search.</div>
            <p className="mt-3 text-sm" style={{ color: "var(--wofy-text-soft)" }}>
              Try clearing the filters or searching a different year.
            </p>
          </div>
        )}
        {grouped.map(([y, group]) => (
          <section key={y} data-testid={`memoriam-year-${y}`}>
            <div className="flex items-baseline gap-4 mb-6">
              <h2 className="font-display text-3xl md:text-4xl">{y}</h2>
              <div className="h-px flex-1" style={{ background: "var(--wofy-border)" }} />
              <span className="text-xs font-mono" style={{ color: "var(--wofy-text-soft)" }}>
                <MapPin className="inline w-3 h-3 mr-1" />
                {group.length} tribute{group.length === 1 ? "" : "s"}
              </span>
            </div>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {group.map((p) => (
                <MemoriamCard key={p.id} person={p} />
              ))}
            </div>
          </section>
        ))}
      </div>

      <div className="mt-16 pt-8 border-t text-xs italic" style={{ borderColor: "var(--wofy-border)", color: "var(--wofy-text-soft)" }}>
        Attribution: biographical facts derive from Wikipedia (CC BY-SA). Editorial paragraphs are
        AI-assisted rewrites for warmth and dignity. Every entry links back to its Wikipedia source.
      </div>
    </div>
  );
}
